"use client";
import React, { useState } from "react";
import MatIcon from "../ui/MatIcon";
import AnimatedSection from "../AnimatedSection";
import ImageBlock from "./ImageBlock";
import { Crew, SHOW_DIRECTORS } from "./data";

interface CrewSectionProps {
  crew: Crew;
  index: number;
}

/**
 * One crew row on the /crews index page. Paper (light) and plum (dark) rows
 * alternate; dark rows lead with the image column. "Learn more" expands an
 * inline panel; "Full crew page" navigates to the crew's route.
 */
const CrewSection: React.FC<CrewSectionProps> = ({ crew, index }) => {
  const dark = index % 2 === 1;
  const [open, setOpen] = useState(false);

  const heading = dark ? "#F1E7EE" : "#2A1435";
  const taglineColor = dark ? "#D6A7E0" : "#9B6BAF";
  const bodyColor = dark ? "#CBB9D3" : "#4A3854";
  const panelBorder = dark ? "rgba(255,255,255,0.14)" : "rgba(42,20,53,0.14)";

  const textColumn = (
    <div data-component-id="src/components/crews/CrewSection.tsx:28:4" data-component-path="src/components/crews/CrewSection.tsx" data-component-line="28" data-component-end-line="128" data-component-file="CrewSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" style={{ flex: "1 1 400px" }}>
      <div data-component-id="src/components/crews/CrewSection.tsx:29:6" data-component-path="src/components/crews/CrewSection.tsx" data-component-line="29" data-component-end-line="50" data-component-file="CrewSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%22%7D" className="flex items-center" style={{ gap: 14, marginBottom: 18 }}>
        <div data-component-id="src/components/crews/CrewSection.tsx:30:8" data-component-path="src/components/crews/CrewSection.tsx" data-component-line="30" data-component-end-line="43" data-component-file="CrewSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22rounded-full%20flex%20items-center%20justify-center%20flex-shrink-0%22%7D"
          className="rounded-full flex items-center justify-center flex-shrink-0"
          style={{
            width: 46,
            height: 46,
            background: dark ? "rgba(199,122,210,0.16)" : "#EBDFF0",
          }}
        >
          <MatIcon data-component-id="src/components/crews/CrewSection.tsx:38:10" data-component-path="src/components/crews/CrewSection.tsx" data-component-line="38" data-component-end-line="42" data-component-file="CrewSection.tsx" data-component-name="MatIcon" data-component-content="%7B%22elementName%22%3A%22MatIcon%22%7D"
            name={crew.icon}
            size={24}
            style={{ color: dark ? "#D6A7E0" : "#241031" }}
          />
        </div>
        <span data-component-id="src/components/crews/CrewSection.tsx:44:8" data-component-path="src/components/crews/CrewSection.tsx" data-component-line="44" data-component-end-line="49" data-component-file="CrewSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22crew-eyebrow%22%7D"
          className="crew-eyebrow"
          style={{ color: "var(--accent)", letterSpacing: "0.16em" }}
        >
          {crew.number}
        </span>
      </div>

      <h3 data-component-id="src/components/crews/CrewSection.tsx:52:6" data-component-path="src/components/crews/CrewSection.tsx" data-component-line="52" data-component-end-line="63" data-component-file="CrewSection.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-geist%22%7D"
        className="font-geist"
        style={{
          fontSize: "clamp(30px, 4vw, 46px)",
          fontWeight: 500,
          lineHeight: 1.05,
          color: heading,
          margin: 0,
        }}
      >
        {crew.name}
      </h3>

      <p data-component-id="src/components/crews/CrewSection.tsx:65:6" data-component-path="src/components/crews/CrewSection.tsx" data-component-line="65" data-component-end-line="75" data-component-file="CrewSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-geist%20italic%22%7D"
        className="font-geist italic"
        style={{
          fontSize: "clamp(19px, 2.3vw, 24px)",
          lineHeight: 1.3,
          color: taglineColor,
          margin: "12px 0 20px",
        }}
      >
        {crew.tagline}
      </p>

      <p data-component-id="src/components/crews/CrewSection.tsx:77:6" data-component-path="src/components/crews/CrewSection.tsx" data-component-line="77" data-component-end-line="88" data-component-file="CrewSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-satoshi%22%7D"
        className="font-satoshi"
        style={{
          fontSize: 17,
          lineHeight: 1.7,
          color: bodyColor,
          maxWidth: "46ch",
          margin: 0,
        }}
      >
        {crew.body}
      </p>

      <div data-component-id="src/components/crews/CrewSection.tsx:90:6" data-component-path="src/components/crews/CrewSection.tsx" data-component-line="90" data-component-end-line="127" data-component-file="CrewSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-wrap%20items-center%22%7D" className="flex flex-wrap items-center" style={{ gap: 14, marginTop: 26 }}>
        {SHOW_DIRECTORS && crew.director && (
          <span data-component-id="src/components/crews/CrewSection.tsx:92:10" data-component-path="src/components/crews/CrewSection.tsx" data-component-line="92" data-component-end-line="116" data-component-file="CrewSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22inline-flex%20items-center%20rounded-pill%22%7D"
            className="inline-flex items-center rounded-pill"
            style={{
              gap: 10,
              padding: "7px 14px",
              border: `1px solid ${dark ? "rgba(255,255,255,0.28)" : "rgba(42,20,53,0.24)"}`,
            }}
          >
            <span data-component-id="src/components/crews/CrewSection.tsx:100:12" data-component-path="src/components/crews/CrewSection.tsx" data-component-line="100" data-component-end-line="109" data-component-file="CrewSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-spacemono%20%22%2C%22textContent%22%3A%22Director%22%7D"
              className="font-spacemono "
              style={{
                fontSize: 11,
                letterSpacing: "0.14em",
                color: dark ? "#CBB4D5" : "#8A7794",
              }}
            >
              Director
            </span>
            <span data-component-id="src/components/crews/CrewSection.tsx:110:12" data-component-path="src/components/crews/CrewSection.tsx" data-component-line="110" data-component-end-line="115" data-component-file="CrewSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-satoshi%20font-semibold%22%7D"
              className="font-satoshi font-semibold"
              style={{ fontSize: 14, color: heading }}
            >
              {crew.director}
            </span>
          </span>
        )}
        <button data-component-id="src/components/crews/CrewSection.tsx:118:8" data-component-path="src/components/crews/CrewSection.tsx" data-component-line="118" data-component-end-line="126" data-component-file="CrewSection.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%7D"
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-controls={`crew-panel-${crew.slug}`}
          className={`crew-btn ${dark ? "crew-btn--outline-dark" : "crew-btn--outline-light"}`}
        >
          {open ? "Close ↑" : "Learn more →"}
        </button>
      </div>
    </div>
  );

  const imageColumn = (
    <ImageBlock data-component-id="src/components/crews/CrewSection.tsx:132:4" data-component-path="src/components/crews/CrewSection.tsx" data-component-line="132" data-component-end-line="139" data-component-file="CrewSection.tsx" data-component-name="ImageBlock" data-component-content="%7B%22elementName%22%3A%22ImageBlock%22%7D"
      dark={dark}
      caption={crew.image.caption}
      src={crew.image.src}
      alt={crew.image.alt}
      minHeight={360}
      style={{ flex: "1 1 360px" }}
    />
  );

  return (
    <section data-component-id="src/components/crews/CrewSection.tsx:143:4" data-component-path="src/components/crews/CrewSection.tsx" data-component-line="143" data-component-end-line="232" data-component-file="CrewSection.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%7D" style={{ background: dark ? "#241031" : "#F5EFE8" }}>
      <div data-component-id="src/components/crews/CrewSection.tsx:144:6" data-component-path="src/components/crews/CrewSection.tsx" data-component-line="144" data-component-end-line="231" data-component-file="CrewSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-%5B1180px%5D%20mx-auto%22%7D"
        className="max-w-[1180px] mx-auto"
        style={{ padding: dark ? "88px 32px" : "72px 32px" }}
      >
        <AnimatedSection data-component-id="src/components/crews/CrewSection.tsx:148:8" data-component-path="src/components/crews/CrewSection.tsx" data-component-line="148" data-component-end-line="162" data-component-file="CrewSection.tsx" data-component-name="AnimatedSection" data-component-content="%7B%22elementName%22%3A%22AnimatedSection%22%7D">
          <div data-component-id="src/components/crews/CrewSection.tsx:149:10" data-component-path="src/components/crews/CrewSection.tsx" data-component-line="149" data-component-end-line="161" data-component-file="CrewSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-wrap%20items-center%22%7D" className="flex flex-wrap items-center" style={{ gap: 56 }}>
            {dark ? (
              <>
                {imageColumn}
                {textColumn}
              </>
            ) : (
              <>
                {textColumn}
                {imageColumn}
              </>
            )}
          </div>
        </AnimatedSection>

        {/* Learn-more panel — collapses fully (border and spacing included) */}
        <div data-component-id="src/components/crews/CrewSection.tsx:165:8" data-component-path="src/components/crews/CrewSection.tsx" data-component-line="165" data-component-end-line="230" data-component-file="CrewSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
          id={`crew-panel-${crew.slug}`}
          className={`crew-panel ${open ? "open" : ""}`}
        >
          <div data-component-id="src/components/crews/CrewSection.tsx:169:10" data-component-path="src/components/crews/CrewSection.tsx" data-component-line="169" data-component-end-line="229" data-component-file="CrewSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
            <div data-component-id="src/components/crews/CrewSection.tsx:170:12" data-component-path="src/components/crews/CrewSection.tsx" data-component-line="170" data-component-end-line="228" data-component-file="CrewSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
              style={{
                marginTop: 52,
                borderTop: `1px solid ${panelBorder}`,
                paddingTop: 44,
              }}
            >
              <span data-component-id="src/components/crews/CrewSection.tsx:177:14" data-component-path="src/components/crews/CrewSection.tsx" data-component-line="177" data-component-end-line="186" data-component-file="CrewSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22crew-eyebrow%22%2C%22textContent%22%3A%22Inside%20the%22%7D"
                className="crew-eyebrow"
                style={{
                  color: dark ? "#D6A7E0" : "#9B6BAF",
                  letterSpacing: "0.18em",
                  marginBottom: 28,
                }}
              >
                Inside the {crew.name}
              </span>
              <div data-component-id="src/components/crews/CrewSection.tsx:187:14" data-component-path="src/components/crews/CrewSection.tsx" data-component-line="187" data-component-end-line="220" data-component-file="CrewSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%22%7D"
                className="grid"
                style={{
                  gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
                  gap: 34,
                }}
              >
                {crew.learnMore.map((feature) => (
                  <div data-component-id="src/components/crews/CrewSection.tsx:195:18" data-component-path="src/components/crews/CrewSection.tsx" data-component-line="195" data-component-end-line="218" data-component-file="CrewSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" key={feature.title}>
                    <h4 data-component-id="src/components/crews/CrewSection.tsx:196:20" data-component-path="src/components/crews/CrewSection.tsx" data-component-line="196" data-component-end-line="206" data-component-file="CrewSection.tsx" data-component-name="h4" data-component-content="%7B%22elementName%22%3A%22h4%22%2C%22className%22%3A%22font-geist%22%7D"
                      className="font-geist"
                      style={{
                        fontSize: 20,
                        fontWeight: 500,
                        color: heading,
                        margin: 0,
                      }}
                    >
                      {feature.title}
                    </h4>
                    <p data-component-id="src/components/crews/CrewSection.tsx:207:20" data-component-path="src/components/crews/CrewSection.tsx" data-component-line="207" data-component-end-line="217" data-component-file="CrewSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-satoshi%22%7D"
                      className="font-satoshi"
                      style={{
                        fontSize: 15,
                        lineHeight: 1.65,
                        color: bodyColor,
                        margin: "10px 0 0",
                      }}
                    >
                      {feature.body}
                    </p>
                  </div>
                ))}
              </div>
              <a data-component-id="src/components/crews/CrewSection.tsx:221:14" data-component-path="src/components/crews/CrewSection.tsx" data-component-line="221" data-component-end-line="227" data-component-file="CrewSection.tsx" data-component-name="a" data-component-content="%7B%22elementName%22%3A%22a%22%2C%22textContent%22%3A%22Full%20crew%20page%20%E2%86%92%22%7D"
                href={crew.href}
                className={`crew-btn ${dark ? "crew-btn--accent" : "crew-btn--ink"}`}
                style={{ marginTop: 36 }}
              >
                Full crew page →
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CrewSection;
